import { Stack, Typography, useTheme } from "@mui/material";
import { DownArrowIcon, DropdownMenu } from "components";

interface PeriodMenuProps<TValue extends string> {
  options: Array<{ label: string; value: TValue }>;
  value: TValue;
  onChange: (value: TValue) => void;
  /** Prefixed to the aria-label so screen readers can tell menus apart. */
  ariaLabel: string;
}

/**
 * Inline "This month ⌄" style dropdown used by the overview header and by every
 * Activity card. Thin, type-safe wrapper over the shared `DropdownMenu` — it only
 * supplies the trigger and the compact menu styling. Generic over the value so
 * each caller keeps its own period vocabulary.
 */
export const PeriodMenu = <TValue extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: PeriodMenuProps<TValue>) => {
  const theme = useTheme();

  const selectedLabel =
    options.find((option) => option.value === value)?.label ?? "";

  return (
    <DropdownMenu
      options={options.map((option) => ({
        name: option.label,
        value: option.value,
      }))}
      selectedValue={value}
      onOptionClick={(selected) => {
        const match = options.find((option) => option.value === selected.value);
        if (match) {
          onChange(match.value);
        }
      }}
      sx={{ display: "inline-flex" }}
      menuProps={{ transformOrigin: { vertical: "top", horizontal: "right" } }}
      paperSx={{
        minWidth: "140px",
        mt: "4px",
        bgcolor: theme.palette.neutral.main,
        boxShadow: "none",
      }}
      menuListSx={{ py: "4px" }}
      menuListItemSx={{
        minWidth: "140px",
        minHeight: "36px",
        p: 0,
        backgroundColor: "transparent",
      }}
      menuItemBuilder={({ item }) => {
        const isSelected = item.value === value;

        return (
          <Stack
            sx={{
              width: "100%",
              height: "36px",
              px: "12px",
              justifyContent: "center",
              backgroundColor: isSelected
                ? theme.palette.primary.tint
                : "transparent",
            }}
          >
            <Typography
              variant="body-small"
              sx={{
                color: isSelected
                  ? theme.palette.primary.main
                  : theme.palette.lightBg.high,
              }}
            >
              {item.name}
            </Typography>
          </Stack>
        );
      }}
    >
      {(isOpen) => (
        <Stack
          component="button"
          type="button"
          direction="row"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-label={`${ariaLabel}: ${selectedLabel}`}
          sx={{
            alignItems: "center",
            gap: "4px",
            p: "2px 4px",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            backgroundColor: "transparent",
            ":hover": {
              "& .button-label": {
                color: theme.palette.primary.main,
              },
              "& .button-icon": {
                color: theme.palette.primary.main,
              },
              backgroundColor: theme.palette.neutral["005"],
            },
          }}
        >
          <Typography
            variant="button-label"
            className="button-label"
            sx={{
              color: theme.palette.lightBg.high,
            }}
            noWrap
          >
            {selectedLabel}
          </Typography>
          <DownArrowIcon
            className="button-icon"
            sx={{ fontSize: "16px", color: theme.palette.lightBg.medium }}
          />
        </Stack>
      )}
    </DropdownMenu>
  );
};
